import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import CustomToolTip from "@/components/extended/ToolTip";
import { Icons, SidebarIcons } from "@/assets/images";
import {
  SideMenuPage,
  UseSidebarNavigationParams,
  UseSidebarNavigationResult,
} from "@/shared/type/SideMenu.type";
import { useSideMenu } from "./useSideMenu";

export const useSidebarNavigation = ({
  sidebarClose,
  openValue,
  setOpenValue,
  setSidebarClose,
}: UseSidebarNavigationParams): UseSidebarNavigationResult => {
  const location = useLocation();
  const { menuItems } = useSideMenu();
  const [darkTheme, setDarkTheme] = useState(false);
  const [mobileView, setMobileView] = useState<boolean>(
    window.matchMedia("(max-width: 767px)").matches,
  );
  const autoExpandedRef = useRef<number | null>(null);

  const isActiveSubLink = useCallback(
    (href: string | null): boolean => {
      if (!href || href === "#") {
        return false;
      }
      return location.pathname === href;
    },
    [location.pathname],
  );

  const getIcon = useCallback(
    (iconPath: string | null, isActive: boolean): string => {
      if (!iconPath) {
        return "";
      }
      const iconKey = iconPath as keyof typeof SidebarIcons.light;
      if (isActive) {
        return SidebarIcons.dark[iconKey] ?? "";
      }
      return darkTheme
        ? (SidebarIcons.dark[iconKey] ?? "")
        : (SidebarIcons.light[iconKey] ?? "");
    },
    [darkTheme],
  );

  const activeParentId = useMemo((): number | null => {
    for (const item of menuItems) {
      if (
        item.childrenPages.some((child) => child.pageUrl === location.pathname)
      ) {
        return item.id;
      }
    }
    return null;
  }, [menuItems, location.pathname]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.classList.contains("dark");
      setDarkTheme(isDark);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDarkTheme(document.documentElement.classList.contains("dark"));

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const handleMqChange = (e: MediaQueryListEvent): void =>
      setMobileView(e.matches);
    mediaQuery.addEventListener("change", handleMqChange);

    return (): void => {
      observer.disconnect();
      mediaQuery.removeEventListener("change", handleMqChange);
    };
  }, [sidebarClose]);

  useEffect(() => {
    if (activeParentId !== null && autoExpandedRef.current !== activeParentId) {
      const activeIndex = menuItems.findIndex(
        (item) => item.id === activeParentId,
      );
      if (activeIndex !== -1) {
        setOpenValue(`item-${activeIndex}`);
        autoExpandedRef.current = activeParentId;
      }
    }
  }, [activeParentId, menuItems, setOpenValue]);

  const renderCollapsedMenuItem = useCallback(
    (item: SideMenuPage, index: number): React.ReactNode => {
      const isChildActive = item.childrenPages.some((child) =>
        isActiveSubLink(child.pageUrl),
      );
      const icon = getIcon(item.iconPath, isChildActive);

      if (item.childrenPages.length > 0) {
        return (
          <CustomToolTip
            side="right"
            className="py-[8.5px] px-3 text-base tracking-wide side-submenu"
            triggerChild={
              <Link
                className={`p-3 text-sm flex gap-3 items-center w-full ${
                  isChildActive ? "active-class" : "bg-white text-foreground"
                }`}
                aria-expanded={openValue === `item-${index}`}
                aria-label={item.menuName}
                to={item.childrenPages[0].pageUrl ?? "/"}
              >
                {icon && (
                  <img
                    width={20}
                    height={20}
                    className="link-icon size-5"
                    src={icon}
                    alt={`${item.menuName} icon`}
                  />
                )}
                {item.menuName}
              </Link>
            }
            triggerContent={
              <div className="tool-tip-content">
                <ul className="p-2">
                  {item.childrenPages.map((child) => (
                    <li key={child.id}>
                      <Link
                        to={child.pageUrl ?? "#"}
                        className={`w-full inline-block ${
                          location.pathname === child.pageUrl &&
                          "bg-bg-hover font-bold"
                        }`}
                      >
                        {child.menuName}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            }
          />
        );
      }

      return (
        <CustomToolTip
          side="right"
          className="py-[8.5px] px-3 text-base tracking-wide"
          triggerChild={
            <Link
              to={item.pageUrl ?? "#"}
              className={`p-3 text-sm flex gap-3 items-center ${
                isActiveSubLink(item.pageUrl)
                  ? "active-class"
                  : "bg-white text-foreground"
              }`}
            >
              {icon && (
                <img
                  width={20}
                  height={20}
                  className="link-icon size-5"
                  src={icon}
                  alt={`${item.menuName} icon`}
                />
              )}
              {item.menuName}
            </Link>
          }
          triggerContent={item.menuName}
        />
      );
    },
    [isActiveSubLink, getIcon, openValue, location.pathname],
  );

  const renderExpandedMenuItem = useCallback(
    (item: SideMenuPage, index: number): React.ReactNode => {
      const isChildActive = item.childrenPages.some((child) =>
        isActiveSubLink(child.pageUrl),
      );
      const icon = getIcon(item.iconPath, isChildActive);

      if (item.childrenPages.length > 0) {
        return (
          <div
            className={`flex flex-col submenu-wrapper ${
              isChildActive
                ? "bg-active text-white"
                : "bg-white text-foreground"
            }`}
          >
            <Accordion
              type="single"
              collapsible
              value={openValue}
              onValueChange={setOpenValue}
            >
              <AccordionItem value={`item-${index}`}>
                <AccordionTrigger
                  className={`accordian-trigger text-nowrap text-foreground flex gap-3 text-sm tracking-wide items-center px-3 py-2.75 w-full focus-visible:ring-0 border border-transparent focus-visible:border-foreground ${
                    isChildActive ? "active-sublink" : "bg-white"
                  }`}
                >
                  {icon && (
                    <img
                      width={20}
                      height={20}
                      className="link-icon size-5"
                      src={icon}
                      alt={`${item.menuName} icon`}
                    />
                  )}
                  {item.menuName}
                  <img
                    src={Icons.chevronDownIcon}
                    alt="Arrow-down"
                    className={`arrow ml-auto size-3 transition-transform ${
                      openValue === `item-${index}` ? "rotate-180" : ""
                    } ${isChildActive && "invert"}`}
                  />
                </AccordionTrigger>
                <AccordionContent className="pb-0 px-3">
                  {item.childrenPages.map((child) => (
                    <Link
                      key={child.id}
                      className={`p-2 w-full text-sm inline-block last:mb-2.5 !text-wrap 
                      ${isActiveSubLink(child.pageUrl) ? "font-bold opacity-100" : "opacity-70 hover:opacity-100 transition-all duration-300"}
                      ${isChildActive ? "opacity-70 hover:opacity-100 transition-all duration-300" : "opacity-100"}`}
                      to={child.pageUrl ?? "#"}
                      onClick={(): void => {
                        if (mobileView) {
                          setSidebarClose(false);
                          setOpenValue("");
                        }
                      }}
                    >
                      {child.menuName}
                    </Link>
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        );
      }

      return (
        <Link
          to={item.pageUrl ?? "#"}
          onClick={(): void => {
            setSidebarClose(false);
            setOpenValue("");
          }}
          className={`p-3 text-sm flex gap-3 items-center ${
            isActiveSubLink(item.pageUrl)
              ? "active-class"
              : "bg-white text-foreground"
          }`}
        >
          {icon && (
            <img
              width={20}
              height={20}
              className="link-icon size-5"
              src={icon}
              alt={`${item.menuName} icon`}
            />
          )}
          {item.menuName}
        </Link>
      );
    },
    [
      isActiveSubLink,
      getIcon,
      openValue,
      setOpenValue,
      mobileView,
      setSidebarClose,
    ],
  );

  return {
    menuItems,
    mobileView,
    renderCollapsedMenuItem,
    renderExpandedMenuItem,
  };
};
